import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TriangleAlert } from 'lucide-react'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  DiscardChangesModal,
  NotificationPreferenceRow,
  NotificationSectionCard,
  NotificationSectionSkeleton,
} from '@/features/notification-settings/components'
import {
  NOTIFICATION_CHANNELS,
  PREFERENCE_CATEGORIES,
  PREFERENCES_BY_CATEGORY,
} from '@/features/notification-settings/constants'
import {
  useNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from '@/features/notification-settings/hooks'
import { useNotificationSettingsStore } from '@/features/notification-settings/store'
import type {
  NotificationChannel,
  PreferenceKey,
} from '@/features/notification-settings/types'

const NotificationSettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const [showCancelModal, setShowCancelModal] = useState(false)

  const { data, isLoading, error } = useNotificationPreferencesQuery()
  const { mutate, isPending } = useUpdateNotificationPreferencesMutation()

  const {
    formState,
    isDirty,
    initializeState,
    updatePreference,
    saveState,
    cancelChanges,
    reset,
  } = useNotificationSettingsStore()

  useEffect(() => {
    if (data) {
      initializeState(data)
    }
  }, [data, initializeState])

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const handlePreferenceChange = useCallback(
    (key: PreferenceKey, value: NotificationChannel) => {
      updatePreference(key, value)
    },
    [updatePreference]
  )

  const createPreferenceChangeHandler = useCallback(
    (key: PreferenceKey) => (value: NotificationChannel) => {
      handlePreferenceChange(key, value)
    },
    [handlePreferenceChange]
  )

  const handleCancelClick = useCallback(() => {
    if (isDirty) {
      setShowCancelModal(true)
    }
  }, [isDirty])

  const handleDiscardChanges = useCallback(() => {
    cancelChanges()
    setShowCancelModal(false)
  }, [cancelChanges])

  const handleSave = useCallback(() => {
    if (!formState) return

    mutate(formState, {
      onSuccess: () => {
        saveState()
        triggerToast('success', t('notificationSettings.saveSuccess'))
      },
      onError: () => {
        triggerToast('error', t('notificationSettings.saveError'))
      },
    })
  }, [formState, mutate, saveState, t])

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="max-w-md text-center">
          <TriangleAlert className="w-12 h-12 mx-auto mb-4 text-error-600" />
          <h2 className="mb-2 text-lg font-semibold text-gray-light-900">
            {t('common.error')}
          </h2>
          <p className="text-sm text-gray-light-600">
            {t('notificationSettings.loadError')}
          </p>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="max-w-4xl">
          <div className="mb-6">
            <NotificationSectionSkeleton rowCount={3} />
          </div>
          <div className="mb-6">
            <NotificationSectionSkeleton rowCount={1} />
          </div>
          <div className="mb-6">
            <NotificationSectionSkeleton rowCount={1} />
          </div>
        </div>
      )
    }

    if (!formState) {
      return null
    }

    return (
      <div className="max-w-4xl">
        {PREFERENCE_CATEGORIES.map((category) => (
          <div key={category.id} id={`section-${category.id}`}>
            <NotificationSectionCard title={t(category.labelKey)}>
              {PREFERENCES_BY_CATEGORY[category.id].map((config, index) => (
                <NotificationPreferenceRow
                  key={config.key}
                  title={t(config.titleKey)}
                  description={t(config.descriptionKey)}
                  value={formState[config.key] || config.defaultValue}
                  onChange={createPreferenceChangeHandler(config.key)}
                  channelOptions={NOTIFICATION_CHANNELS}
                  showSeparator={
                    index < PREFERENCES_BY_CATEGORY[category.id].length - 1
                  }
                />
              ))}
            </NotificationSectionCard>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 bg-white">
        <h1 className="m-0 text-xl font-semibold text-gray-900">
          {t('notificationSettings.pageTitle')}
        </h1>
      </div>
      <div className="flex-1 mt-6">{renderContent()}</div>
      <div className="fixed bottom-0 right-0 z-10 px-6 py-3 bg-white border-t border-b-0 border-solid border-x-0 border-gray-light-200 lg:relative lg:left-0 lg:w-[calc(100%+4rem)] lg:-mx-8 lg:-mb-8">
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outlined"
            onClick={handleCancelClick}
            disabled={!isDirty || isPending}
            aria-label={t('common.cancel')}
            className="w-20 h-10 text-sm font-semibold"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || isPending}
            aria-label={isPending ? t('common.saving') : t('common.save')}
            className="w-[65px] h-10 text-sm font-semibold"
          >
            {isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>

      <DiscardChangesModal
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
        onDiscard={handleDiscardChanges}
      />
    </div>
  )
}

export default NotificationSettingsPage
